if (!customElements.get("quick-view-modal")) {
  customElements.define(
    "quick-view-modal",
    class QuickViewModal extends HTMLElement {
      constructor() {
        super();
        this.modalContent = this.querySelector("#QuickViewModal");
        this.drawerContent = this.querySelector("[data-drawer-content]");
        this.overlay = this.querySelector("#QuickView-Overlay");
        this.openedBy = null;
        this.isOpen = false;

        this.overlay?.addEventListener("click", () => this.hide());
        this.addEventListener("keyup", (event) => {
          if (event.code.toUpperCase() === "ESCAPE") this.hide();
        });

        this.addEventListener("product-info:loaded", ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        if (!this.dataset.moved) {
          this.dataset.moved = true;
          document.body.appendChild(this);
        }
      }

      async hide(preventFocus = false) {
        if (!this.isOpen) return;

        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);

        await this.closeDrawer(preventFocus);
        this.clearColorSchemeClasses();
        setTimeout(() => {
          if (this.modalContent) this.modalContent.innerHTML = "";
        }, 300);
      }

      show(opener) {
        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        opener.querySelector(".loading__spinner")?.classList.remove("hidden");

        const productUrl = `${opener.getAttribute("data-product-url").split("?")[0]}?view=quick_add`;

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const productElement = responseHTML.querySelector("product-info");

            if (!productElement) return;

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(
              this.modalContent,
              productElement.outerHTML
            );

            if (
              typeof window.compareColor === "undefined" &&
              !customElements.get("compare-color")
            ) {
              try {
                window.compareColor();
              } catch (e) {
                /* no-op */
              }
            }

            if (window.Shopify && Shopify.PaymentButton)
              Shopify.PaymentButton.init();
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            return this.openDrawer(opener);
          })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            opener.querySelector(".loading__spinner")?.classList.add("hidden");
          });
      }

      async openDrawer(opener) {
        if (opener) this.openedBy = opener;

        setTimeout(() => this.classList.add("active"));
        this.classList.add("open");
        this.isOpen = true;

        document.body.classList.add("overflow-hidden", "quick-view-open");
        document.documentElement.setAttribute("scroll-lock", "");

        const dir = this.getAttribute("data-drawer-direction") || "right";
        const contentElement = this.querySelector("[data-drawer-content]");

        await Motion.timeline([
          [
            contentElement,
            {
              opacity: [0, 1],
              transform:
                dir === "left"
                  ? ["translateX(-100%)", "translateX(0)"]
                  : ["translateX(100%)", "translateX(0)"],
            },
            { duration: 0.3, easing: [0.61, 0.22, 0.23, 1], at: "-0.05" },
          ],
        ]).finished;

        const containerToTrapFocusOn = this.querySelector(".drawer__inner");
        const focusElement = this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      }

      async closeDrawer(preventFocus = false) {
        if (preventFocus) this.openedBy = null;
        else removeTrapFocus(this.openedBy);

        document.body.classList.remove("overflow-hidden", "quick-view-open");
        document.documentElement.removeAttribute("scroll-lock");

        const dir = this.getAttribute("data-drawer-direction") || "right";
        const contentElement = this.querySelector("[data-drawer-content]");

        await Motion.timeline([
          [
            contentElement,
            {
              opacity: [1, 0],
              transform:
                dir === "left"
                  ? ["translateX(0)", "translateX(-100%)"]
                  : ["translateX(0)", "translateX(100%)"],
            },
            { duration: 0.3, easing: [0.61, 0.22, 0.23, 1] },
          ],
        ]).finished;

        this.classList.remove("open", "active");
        this.isOpen = false;
      }

      clearColorSchemeClasses() {
        if (!this.drawerContent) return;
        Array.from(this.drawerContent.classList).forEach((className) => {
          if (className.startsWith("color-") || className === "gradient") {
            this.drawerContent.classList.remove(className);
          }
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (
            classApplied.startsWith("color-") ||
            classApplied === "gradient"
          ) {
            this.drawerContent?.classList.add(classApplied);
          }
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute("data-update-url", "false");
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector(
          "pickup-availability"
        );
        if (pickupAvailability) pickupAvailability.remove();

        const shareButton = productElement.querySelector("share-button");
        if (shareButton) shareButton.remove();

        const productModal = productElement.querySelector("product-modal");
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll("modal-dialog");
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        const sideDrawerOpener =
          productElement.querySelectorAll("side-drawer-opener");
        if (sideDrawerOpener)
          sideDrawerOpener.forEach((button) => button.remove());

        const sideDrawer = productElement.querySelectorAll("side-drawer");
        if (sideDrawer) sideDrawer.forEach((drawer) => drawer.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickview-${sectionId}`;

        productElement.innerHTML = productElement.innerHTML.replaceAll(
          oldId,
          newId
        );
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(
              attribute.name,
              attribute.value.replace(oldId, newId)
            );
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector(
          '[id^="Slider-Gallery"]'
        );
        if (!galleryList) return;

        galleryList.setAttribute("role", "presentation");
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute("role", "presentation"));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector(".product");
        const desktopColumns =
          product?.classList.contains("product--columns");
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll(".product__media img");
        if (!mediaImages.length) return;

        let mediaImageSizes =
          "(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)";

        if (product.classList.contains("product--medium")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "605px");
        } else if (product.classList.contains("product--small")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "495px");
        }

        mediaImages.forEach((img) =>
          img.setAttribute("sizes", mediaImageSizes)
        );
      }
    }
  );
}
